import { nanoid } from 'nanoid';
import {
  FeedbackQuestion,
  Lesson,
  ModuleDraft,
  MultipleChoiceQuestion,
  mapFeedbackTypeToAnswerFormat
} from '../../../../../../../components/admin3/newProgram/masterclass/types';
import { GeneratedLesson, MCQ } from '../../../../aimd-v2/types';

/** Only persisted template levels/tasks carry real Mongo ObjectIds — locally
 *  added drafts have nanoid placeholders the task-scoped endpoints would reject. */
export const isObjectId = (v?: string): boolean => Boolean(v && /^[0-9a-fA-F]{24}$/.test(v));

/** Below this much visible text, "Improve with AI" drafts the lesson from its
 *  title + the admin's notes instead of rewriting it. Keep in step with the BE
 *  (elliottService MIN_IMPROVABLE_CONTENT_CHARS). */
const MIN_IMPROVABLE_CONTENT_CHARS = 80;

export const lessonNeedsDraft = (description?: string): boolean =>
  (description || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim().length < MIN_IMPROVABLE_CONTENT_CHARS;

/** One MCQ → the `{ ques, options: [{ optionText, ans }], _id? }` task-goal shape. */
function buildMcqPayload(mcq: MultipleChoiceQuestion, mcqIdx: number): Record<string, any> {
  const baseOptions = mcq.options.length > 0 ? mcq.options : [{ id: nanoid(), label: 'Option 1' }];
  const paddedOptions =
    baseOptions.length >= 4
      ? baseOptions
      : [
          ...baseOptions,
          ...Array.from({ length: 4 - baseOptions.length }).map((_, idx) => ({
            id: nanoid(),
            label: `Option ${baseOptions.length + idx + 1}`
          }))
        ];
  const optionTexts = paddedOptions.map((opt, idx) => opt.label || `Option ${idx + 1}`);
  const correctIndex =
    mcq.correctOptionId && paddedOptions.findIndex((opt) => opt.id === mcq.correctOptionId) !== -1
      ? paddedOptions.findIndex((opt) => opt.id === mcq.correctOptionId)
      : 0;

  const payload: Record<string, any> = {
    ques: mcq.prompt || `MCQ ${mcqIdx + 1}`,
    options: optionTexts.map((text, idx) => ({ optionText: text, ans: idx === correctIndex })),
    // One reasoning per option, aligned with the options sent (padded to match).
    optionReasonings: paddedOptions.map((_, idx) => (mcq.optionReasonings || [])[idx] || '')
  };
  if (mcq._id) payload._id = mcq._id;
  return payload;
}

/** One feedback question → the `{ menteeQues, menteeAnsType, _id? }` task-goal shape. */
function buildMenteeQuestionPayload(question: FeedbackQuestion, idx: number): Record<string, any> {
  const payload: Record<string, any> = {
    menteeQues: question.prompt || `Question ${idx + 1}`,
    menteeAnsType: mapFeedbackTypeToAnswerFormat(question.type)
  };
  if (question._id) payload._id = question._id;
  return payload;
}

/** One lesson → a task payload. Deliberately OMITS `elliott` (see below), except once for a lesson an Undo put back. */
function buildTaskPayload(lesson: Lesson, lessonIndex: number): Record<string, any> {
  const payload: Record<string, any> = {
    taskName: lesson.title || `Lesson ${lessonIndex + 1}`,
    taskDescription: lesson.description || 'Lesson description',
    taskPosition: lessonIndex + 1,
    MCQ: lesson.multipleChoiceQuestions.length > 0 ? lesson.multipleChoiceQuestions.map(buildMcqPayload) : [],
    menteeQuestions:
      lesson.feedbackQuestions.length > 0 ? lesson.feedbackQuestions.map(buildMenteeQuestionPayload) : [],
    menteeAsmtQues: 'Have you completed this lesson?',
    menteeAsmtAnsType: 'Yes, No',
    menteeAsmtAnsOptions: ['Yes', 'No'],
    libraryArticles: lesson.materials
      .filter((material) => ['library', 'ai'].includes((material.source || 'custom') as string))
      .map((material) => material.articleId || material.id)
      .filter(Boolean),
    startDate: 0,
    endDate: 0
  };
  // AI-generation provenance (PRD §9.3) — resend on template saves so a full
  // structural save doesn't wipe what the task already carries.
  if (lesson.reasoning) payload.reasoning = lesson.reasoning;
  if (lesson.learningOutcomesServed?.length) payload.learningOutcomesServed = lesson.learningOutcomesServed;
  if (lesson.ragDocumentTitles?.length) payload.ragDocumentTitles = lesson.ragDocumentTitles;
  // A build-flow lesson's tags (docs/PRD-capability-lesson-roleplays.md R8): a lesson added or retagged
  // here has them only in the editor, and one an Undo put back no longer has them on the server.
  if (lesson.capabilityId !== undefined || lesson.outcomes !== undefined || lesson.roleplay !== undefined) {
    payload.capabilityId = lesson.capabilityId ?? null;
    payload.outcomes = lesson.capabilityId ? [] : lesson.outcomes ?? [];
    payload.roleplay = Boolean(lesson.capabilityId) && lesson.roleplay === true;
  }
  // A lesson an Undo put back: the saved draft no longer has its Elliott sections, so send them once.
  if (lesson.resendElliott && lesson.elliott) payload.elliott = lesson.elliott;
  if (lesson.sourceId) payload.id = lesson.sourceId;
  return payload;
}

/**
 * Builds the `updateGroupTasks` levels payload from the design builder's
 * `ModuleDraft[]` state — a faithful copy of `ManualProgramBuilder.buildLevelsPayload`
 * so the live-template save is identical to the manual builder's. Crucially it
 * OMITS `elliott`, so the per-task Elliott sections (persisted immediately via the
 * task endpoints) are preserved by the BE's merge-by-id instead of wiped on save.
 */
export function buildTemplateLevelsPayload(sourceModules: ModuleDraft[]) {
  return sourceModules.map((module, moduleIndex) => {
    const modulePayload: Record<string, any> = {
      levelName: module.title || `Module ${moduleIndex + 1}`,
      levelDescription: module.objective || '',
      level: moduleIndex + 1,
      CheckInWithMentor: 'no',
      startDate: module.startDate || { type: 'activeNow', value: 0 },
      endDate: module.endDate || { type: 'activeNow', value: 0 }
    };
    if (module.sourceId) modulePayload.id = module.sourceId;
    modulePayload.tasks = module.lessons.map(buildTaskPayload);
    return modulePayload;
  });
}

/**
 * Adapts a `Lesson` (ModuleDraft shape) into the `GeneratedLesson` shape that
 * `LessonContentView` renders. `content` comes from the lesson description; the
 * live quiz lives in `task.MCQ` (→ `mcqs`), so `elliott.quiz` is stripped to
 * stop `buildMcqList` from topping up duplicate questions.
 */
export function lessonToGeneratedLesson(lesson: Lesson): GeneratedLesson {
  const mcqs: MCQ[] = (lesson.multipleChoiceQuestions || []).map((q) => {
    const options = (q.options || []).map((o) => o.label);
    const correctIdx = q.correctOptionId ? (q.options || []).findIndex((o) => o.id === q.correctOptionId) : 0;
    return {
      question: q.prompt || '',
      options,
      correct_answer: String(correctIdx >= 0 ? correctIdx : 0),
      optionReasonings: q.optionReasonings || []
    };
  });

  const elliott = lesson.elliott ? { ...lesson.elliott, quiz: [] } : null;

  return {
    title: lesson.title,
    reasoning: lesson.reasoning || '',
    learningOutcomesServed: lesson.learningOutcomesServed || [],
    ragDocumentTitles: lesson.ragDocumentTitles || [],
    status: 'completed',
    content: lesson.description || '',
    materials: [],
    mcq: null,
    mcqs,
    usedRag: (lesson.ragDocumentTitles || []).length > 0,
    generatedAt: null,
    elliott: elliott as GeneratedLesson['elliott']
  };
}

/**
 * Maps `LessonContentView`'s edited `MCQ[]` back onto `Lesson.multipleChoiceQuestions`,
 * preserving existing question / option ids by position so a template save keeps
 * question identity where the counts line up. `correct_answer` is the numeric
 * index string the editor emits.
 */
export function mcqsToMultipleChoice(mcqs: MCQ[], existing: MultipleChoiceQuestion[] = []): MultipleChoiceQuestion[] {
  return mcqs.map((m, i) => {
    const prev = existing[i];
    const options = (m.options || []).map((label, oi) => ({
      id: prev?.options?.[oi]?.id || nanoid(),
      label
    }));
    const correctIdx = Number(m.correct_answer);
    const correctOptionId = (Number.isInteger(correctIdx) && options[correctIdx]?.id) || options[0]?.id || undefined;
    return {
      id: prev?.id || nanoid(),
      _id: prev?._id,
      prompt: m.question,
      options,
      correctOptionId,
      optionReasonings: (m.options || []).map((_, oi) => (m.optionReasonings || [])[oi] || '')
    };
  });
}
